/**
 * oddit-section-main-product
 * Interactive behaviors: accordions, carousel, thumbnail selection,
 * color swatches, size selection, variant matching
 */

(function () {
  'use strict';

  function centerThumbnail(thumb, container) {
    if (!thumb || !container) return;
    var thumbTop = thumb.offsetTop;
    var thumbHeight = thumb.offsetHeight;
    var containerHeight = container.clientHeight;
    var scrollTarget = thumbTop - containerHeight / 2 + thumbHeight / 2;
    container.scrollTo({ top: scrollTarget, behavior: 'smooth' });
  }

  function initSection(sectionEl) {
    if (!sectionEl) return;

    /*
     * PDP gallery images intentionally render without an alt attribute so
     * accessiBe can provide its own generated descriptions. This cleanup only
     * runs during section initialization; it does not observe later changes,
     * so it will not remove alt text subsequently added by accessiBe.
     */
    sectionEl
      .querySelectorAll(
        '.oddit-section-main-product__main-image img, ' +
        '.oddit-section-main-product__thumbnail img, ' +
        '.oddit-section-main-product__carousel-slide img'
      )
      .forEach(function (image) {
        image.removeAttribute('alt');
      });

    initMobileZoom(sectionEl);

    /* ==========================================================================
       Product Variants Data
       ========================================================================== */

    var variants = [];
    var variantJsonEl = sectionEl.querySelector('[data-product-variants]');
    if (variantJsonEl) {
      try {
        variants = JSON.parse(variantJsonEl.textContent);
      } catch (e) {
        console.error('Failed to parse product variants JSON:', e);
      }
    }

    // Track currently selected options keyed by position (1-indexed)
    var selectedOptions = {};

    // Initialize selected options from active swatches/sizes
    sectionEl
      .querySelectorAll('.oddit-section-main-product__swatch--active, .oddit-section-main-product__size-option--active')
      .forEach(function (el) {
        var pos = el.getAttribute('data-option-position');
        var val = el.getAttribute('data-option-value');
        if (pos && val) {
          selectedOptions[pos] = val;
        }
      });

    /* ==========================================================================
       Variant Matching
       ========================================================================== */

    function findMatchingVariant() {
      var positionKeys = Object.keys(selectedOptions);
      if (positionKeys.length === 0) return null;

      for (var i = 0; i < variants.length; i++) {
        var variant = variants[i];
        var match = true;
        for (var j = 0; j < positionKeys.length; j++) {
          var pos = parseInt(positionKeys[j], 10);
          var selectedVal = (selectedOptions[positionKeys[j]] || '').trim();
          var variantVal = (variant.options[pos - 1] || '').trim();
          if (variantVal !== selectedVal) {
            match = false;
            break;
          }
        }
        if (match) return variant;
      }
      return null;
    }

    function dispatchVariantChange(variant) {
      var detail = {
        selectedOptions: Object.assign({}, selectedOptions),
        variant: variant,
      };
      document.dispatchEvent(new CustomEvent('oddit:variant:change', { detail: detail }));
    }

    function updateMainProductUI(variant) {
      // Update hidden variant ID input
      var variantInput = sectionEl.querySelector('input[name="id"]');
      if (variantInput && variant) {
        variantInput.value = variant.id;
      }

      // Update URL query parameter
      if (variant && variant.id) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      // Update ATC button. When the product has a size selector and no size
      // has been picked yet, surface "Choose a size" instead of "Sold out" \u2014
      // a sold-out *default* shouldn't read like the whole product is gone.
      var atcButton = sectionEl.querySelector('.oddit-section-main-product__atc-button');
      if (atcButton) {
        var hasSizeSelector = !!sectionEl.querySelector('.oddit-section-main-product__size-option');
        var sizeSelected = !!sectionEl.querySelector('.oddit-section-main-product__size-option--active');
        if (hasSizeSelector && !sizeSelected) {
          atcButton.disabled = true;
          atcButton.textContent = 'CHOOSE A SIZE';
        } else if (variant && variant.available) {
          atcButton.disabled = false;
          atcButton.textContent = 'ADD TO BAG';
        } else {
          atcButton.disabled = true;
          atcButton.textContent = 'SOLD OUT';
        }
      }

      // Update price display — mirror the Liquid markup so the sale tag
      var priceEl = sectionEl.querySelector('.oddit-section-main-product__price');
      if (priceEl && variant) {
        var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        priceEl.classList.toggle('oddit-section-main-product__price--on-sale', !!onSale);
        if (onSale) {
          var discountPercent = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
          priceEl.innerHTML =
            '<span class="oddit-section-main-product__price-compare">' + formatMoney(variant.compare_at_price) + '</span>' +
            '<span class="oddit-section-main-product__price-sale">' + variant.price_formatted + '</span>' +
            '<span class="oddit-section-main-product__sale-tag">SALE -' + discountPercent + '%</span>';
        } else {
          priceEl.textContent = variant.price_formatted;
        }
      }

      // Update gallery to show the variant's hero image. ELA-156: sticks to
      // `variant.featured_image` (the merchandiser-designated hero in
      // Shopify admin) — the industry-standard signal. If a variant's hero
      // looks wrong on PDP, the fix is in product admin (reassign the
      // variant's image), not here.
      if (variant && variant.featured_image) {
        var targetSrc = variant.featured_image.src;

        // Desktop: find the thumbnail whose data-full-src matches the
        // variant's featured image and mark it active.
        var allThumbs = sectionEl.querySelectorAll('.oddit-section-main-product__thumbnail');
        var matchedIndex = -1;
        var targetFile = targetSrc.split('?')[0].split('/').pop();
        allThumbs.forEach(function (thumb, i) {
          var thumbFile = (thumb.getAttribute('data-full-src') || '').split('?')[0].split('/').pop();
          if (thumbFile === targetFile) {
            matchedIndex = i;
          }
        });

        if (matchedIndex !== -1) {
          // Simulate thumbnail click for desktop
          allThumbs.forEach(function (t) {
            t.classList.remove('oddit-section-main-product__thumbnail--active');
          });
          allThumbs[matchedIndex].classList.add('oddit-section-main-product__thumbnail--active');

          // Scroll thumbnail to center
          var thumbContainerEl = sectionEl.querySelector('.oddit-section-main-product__thumbnails');
          centerThumbnail(allThumbs[matchedIndex], thumbContainerEl);
        }

        // Update main image (desktop). Don't pause videos here either —
        // they stay running in the background so a return to a video
        // thumb is instant.
        var mainImg = sectionEl.querySelector('.oddit-section-main-product__main-image img');
        var allMainVids = sectionEl.querySelectorAll('.oddit-section-main-product__main-video');
        allMainVids.forEach(function (v) {
          v.classList.remove('is-active');
        });
        if (mainImg) mainImg.style.display = '';
        if (mainImg) {
          mainImg.src = variant.featured_image.src;
          mainImg.srcset = variant.featured_image.srcset;
          mainImg.removeAttribute('alt');
        }

        // Mobile: scroll carousel to matching slide
        if (matchedIndex !== -1) {
          var mobileTrack = sectionEl.querySelector('.oddit-section-main-product__carousel-track');
          var mobileSlides = sectionEl.querySelectorAll('.oddit-section-main-product__carousel-slide');
          if (mobileTrack && mobileSlides.length > matchedIndex) {
            var slideWidth = mobileSlides[0].offsetWidth;
            var gap = 8;
            mobileTrack.scrollTo({
              left: matchedIndex * (slideWidth + gap),
              behavior: 'smooth',
            });
          }
        }
      }
    }

    function formatMoney(cents) {
      // Basic money formatter — uses Shopify's money format if available
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }
      return '$' + (cents / 100).toFixed(2);
    }

    /* ==========================================================================
       Accordions
       ========================================================================== */

    var accordionHeaders = sectionEl.querySelectorAll('.oddit-section-main-product__accordion-header');

    accordionHeaders.forEach(function (header) {
      header.addEventListener('click', function () {
        var isExpanded = this.getAttribute('aria-expanded') === 'true';
        this.setAttribute('aria-expanded', String(!isExpanded));
      });
    });

    /* ==========================================================================
       Mobile Carousel
       ========================================================================== */

    var carouselTrack = sectionEl.querySelector('.oddit-section-main-product__carousel-track');
    var carouselDots = sectionEl.querySelectorAll('.oddit-section-main-product__carousel-dot');
    var carouselSlides = sectionEl.querySelectorAll('.oddit-section-main-product__carousel-slide');

    /* Prime every mobile-carousel video on init so the first scroll into
       a video slide plays instantly instead of buffering for ~300ms.
       Skip if the video is already playing (i.e. it's the slide-0 video
       that syncCarouselVideo just started) — re-priming it would pause
       the playback we just kicked off. */
    carouselSlides.forEach(function (slide) {
      var video = slide.querySelector('video');
      if (!video) return;
      try { video.load(); } catch (e) {}
      var primed = false;
      var prime = function () {
        if (primed) return;
        primed = true;
        if (!video.paused) return;
        var p = video.play();
        var stop = function () {
          try { video.pause(); } catch (e) {}
          try { video.currentTime = 0; } catch (e) {}
        };
        if (p && typeof p.then === 'function') p.then(stop, stop); else stop();
      };
      if (video.readyState >= 2) {
        prime();
      } else {
        video.addEventListener('loadeddata', prime, { once: true });
      }
    });

    function syncCarouselVideo(activeIndex) {
      carouselSlides.forEach(function (slide, i) {
        var video = slide.querySelector('video');
        if (!video) return;
        if (i === activeIndex) {
          video.play().catch(function () {});
        } else {
          video.pause();
        }
      });
    }

    if (carouselTrack && carouselDots.length > 0) {
      var scrollTimeout;
      carouselTrack.addEventListener('scroll', function () {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(function () {
          var scrollLeft = carouselTrack.scrollLeft;
          var slideWidth = carouselSlides[0] ? carouselSlides[0].offsetWidth : 1;
          var gap = 8;
          var activeIndex = Math.round(scrollLeft / (slideWidth + gap));

          carouselDots.forEach(function (dot, i) {
            dot.classList.toggle('oddit-section-main-product__carousel-dot--active', i === activeIndex);
          });

          syncCarouselVideo(activeIndex);
        }, 50);
      });

      carouselDots.forEach(function (dot, index) {
        dot.addEventListener('click', function () {
          var slideWidth = carouselSlides[0] ? carouselSlides[0].offsetWidth : 0;
          var gap = 8;
          carouselTrack.scrollTo({
            left: index * (slideWidth + gap),
            behavior: 'smooth',
          });
        });
      });

      // ELA-156: align the mobile carousel + dot to the Liquid-rendered
      // --active thumbnail on init (which is the first-available variant's
      // featured media, or the URL ?variant=<id> variant) so the mobile
      // landing matches the desktop main image instead of always starting
      // at media[0]. Layout-deferred a frame so carousel-track has its
      // final width before slideWidth is measured.
      var initActiveIndex = 0;
      var initActiveThumb = sectionEl.querySelector('.oddit-section-main-product__thumbnail--active');
      if (initActiveThumb) {
        var allInitThumbs = sectionEl.querySelectorAll('.oddit-section-main-product__thumbnail');
        for (var ai = 0; ai < allInitThumbs.length; ai++) {
          if (allInitThumbs[ai] === initActiveThumb) { initActiveIndex = ai; break; }
        }
      }
      if (initActiveIndex > 0) {
        requestAnimationFrame(function () {
          var slideWidth = carouselSlides[0] ? carouselSlides[0].offsetWidth : 0;
          if (slideWidth) {
            // Set scrollLeft directly (not scrollTo with behavior:'instant')
            // for broadest browser support — the carousel needs to be at the
            // right position before the user sees it, not animated in.
            carouselTrack.scrollLeft = initActiveIndex * (slideWidth + 8);
          }
          carouselDots.forEach(function (dot, i) {
            dot.classList.toggle('oddit-section-main-product__carousel-dot--active', i === initActiveIndex);
          });
        });
      }

      syncCarouselVideo(initActiveIndex);
    }

    /* ==========================================================================
       Desktop Thumbnails
       ========================================================================== */

    var thumbnails = sectionEl.querySelectorAll('.oddit-section-main-product__thumbnail');
    var mainImage = sectionEl.querySelector('.oddit-section-main-product__main-image img');

    var thumbContainerForClick = sectionEl.querySelector('.oddit-section-main-product__thumbnails');

    var mainVideos = sectionEl.querySelectorAll('.oddit-section-main-product__main-video');

    /* Start every main-video playing muted at opacity 0 on init and
       never pause them. The pause-after-prime cycle we used before still
       let the browser drop the decoded first frame, so the next play()
       paid a re-decode tax (~50-200ms). Keeping the videos running in
       the background trades a small CPU cost for truly instant thumb
       activation — click just toggles visibility and seeks to 0. */
    mainVideos.forEach(function (v) {
      try { v.load(); } catch (e) {}
      var startBackgroundPlay = function () {
        var p = v.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      };
      if (v.readyState >= 2) {
        startBackgroundPlay();
      } else {
        v.addEventListener('loadeddata', startBackgroundPlay, { once: true });
      }
    });

    function deactivateAllMainVideos() {
      // Visibility-only deactivation: keep videos running in the
      // background so re-activation stays instant. CPU cost is small
      // (1-3 muted videos at opacity 0) and bounded by the PDP scope.
      mainVideos.forEach(function (v) {
        v.classList.remove('is-active');
      });
    }

    function findMainVideoByMediaId(mediaId) {
      if (!mediaId) return null;
      for (var i = 0; i < mainVideos.length; i++) {
        if (mainVideos[i].getAttribute('data-media-id') === String(mediaId)) {
          return mainVideos[i];
        }
      }
      return null;
    }

    thumbnails.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        thumbnails.forEach(function (t) {
          t.classList.remove('oddit-section-main-product__thumbnail--active');
        });
        this.classList.add('oddit-section-main-product__thumbnail--active');
        centerThumbnail(this, thumbContainerForClick);

        var mediaType = this.getAttribute('data-media-type');
        var fullSrc = this.getAttribute('data-full-src');
        var fullSrcset = this.getAttribute('data-full-srcset');

        if (mediaType === 'video' && mainVideos.length) {
          var mediaId = this.getAttribute('data-media-id');
          var target = findMainVideoByMediaId(mediaId);
          if (target) {
            deactivateAllMainVideos();
            target.classList.add('is-active');
            if (mainImage) mainImage.style.display = 'none';

            // Video is already playing in the background (see prime block
            // above) — just seek to 0 so the user sees the clip from the
            // start. Calling play() is a safety net for the rare case
            // where the background play() promise rejected (autoplay
            // policy edge case).
            try { target.currentTime = 0; } catch (e) {}
            var p = target.play();
            if (p && typeof p.catch === 'function') p.catch(function () {});
          }
        } else {
          deactivateAllMainVideos();
          if (mainImage) mainImage.style.display = 'block';
          if (mainImage && fullSrc) {
            mainImage.src = fullSrc;
            if (fullSrcset) mainImage.srcset = fullSrcset;
            mainImage.removeAttribute('alt');
          }
        }
      });
    });

    /* ==========================================================================
       Desktop Hover Zoom — transform-origin follows cursor
       ========================================================================== */

    var mainImageContainer = sectionEl.querySelector('.oddit-section-main-product__main-image');
    if (mainImageContainer) {
      // Zoom quality: hovering magnifies the image to scale(2), but the
      // browser only loads the candidate that fits the un-zoomed 50vw box, so
      // the magnified view can look soft (most on non-retina screens, where
      // 50vw resolves to a ~750w candidate). On first hover, widen the `sizes`
      // hint so the browser pulls the largest srcset candidate for the zoom.
      // One-way — once fetched it stays cached, and initial LCP is untouched
      // because `sizes` starts at 50vw.
      mainImageContainer.addEventListener('mouseenter', function () {
        var img = mainImageContainer.querySelector('img');
        if (!img || img.dataset.zoomUpgraded === 'true') return;
        img.dataset.zoomUpgraded = 'true';
        img.sizes = '(min-width: 990px) 100vw, 100vw';
      });

      mainImageContainer.addEventListener('mousemove', function (e) {
        var img = mainImageContainer.querySelector('img');
        if (!img || img.style.display === 'none') return;
        var rect = mainImageContainer.getBoundingClientRect();
        var x = ((e.clientX - rect.left) / rect.width) * 100;
        var y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = x + '% ' + y + '%';
      });

      mainImageContainer.addEventListener('mouseleave', function () {
        var img = mainImageContainer.querySelector('img');
        if (img) img.style.transformOrigin = 'center center';
      });
    }

    /* ==========================================================================
       Color Swatches
       ========================================================================== */

    var swatches = sectionEl.querySelectorAll('.oddit-section-main-product__swatch');
    var colorValueEl = sectionEl.querySelector('.oddit-section-main-product__selector-value');

    swatches.forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        swatches.forEach(function (s) {
          s.classList.remove('oddit-section-main-product__swatch--active');
        });
        this.classList.add('oddit-section-main-product__swatch--active');

        var colorName = this.getAttribute('data-option-value');
        var position = this.getAttribute('data-option-position');

        if (colorValueEl && colorName) {
          colorValueEl.textContent = colorName;
        }

        if (position && colorName) {
          selectedOptions[position] = colorName;
          updateSizeAvailability();
          var variant = findMatchingVariant();
          // If the previously-selected size doesn't exist for the new color,
          // clear the size selection rather than falling back to the first
          // variant of that color — that fallback can land on a sold-out
          // size and show a misleading "Sold out" before the customer has
          // actually picked anything. Force them to choose a size instead.
          if (!variant) {
            sizeOptions.forEach(function (btn) {
              btn.classList.remove('oddit-section-main-product__size-option--active');
              var btnPos = btn.getAttribute('data-option-position');
              if (btnPos) delete selectedOptions[btnPos];
            });
            // Re-resolve to a color-only variant so the gallery and price
            // still switch to the new color. The button updater sees no
            // active size and renders "Choose a size" regardless.
            variant = findMatchingVariant();
          }
          updateMainProductUI(variant);
          dispatchVariantChange(variant);
        }
      });
    });

    /* ==========================================================================
       Size Options
       ========================================================================== */

    var sizeOptions = sectionEl.querySelectorAll('.oddit-section-main-product__size-option');

    sizeOptions.forEach(function (option) {
      option.addEventListener('click', function () {
        sizeOptions.forEach(function (o) {
          o.classList.remove('oddit-section-main-product__size-option--active');
        });
        this.classList.add('oddit-section-main-product__size-option--active');

        var sizeValue = this.getAttribute('data-option-value');
        var position = this.getAttribute('data-option-position');

        if (position && sizeValue) {
          selectedOptions[position] = sizeValue;
          var variant = findMatchingVariant();
          updateMainProductUI(variant);
          dispatchVariantChange(variant);
        }
      });
    });

    /* ==========================================================================
       Dynamic Size Availability — update when color changes
       ========================================================================== */

    function updateSizeAvailability() {
      // When linked-product swatches are used, the active swatch is an anchor
      // with data-linked-color and no variant-option data attrs — in that case
      // skip color filtering since each color is its own product anyway.
      var activeColor = sectionEl.querySelector('.oddit-section-main-product__swatch--active');
      var colorPos = NaN;
      var colorVal = null;
      if (activeColor) {
        var posAttr = activeColor.getAttribute('data-option-position');
        colorPos = posAttr ? parseInt(posAttr, 10) : NaN;
        colorVal = activeColor.getAttribute('data-option-value');
      }
      var hasColorFilter = !isNaN(colorPos) && colorVal !== null;

      sizeOptions.forEach(function (btn) {
        var sizePos = parseInt(btn.getAttribute('data-option-position'), 10);
        var sizeVal = btn.getAttribute('data-option-value');

        var available = false;
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var sizeMatch = v.options[sizePos - 1] === sizeVal;
          var colorMatch = hasColorFilter ? v.options[colorPos - 1] === colorVal : true;
          if (sizeMatch && colorMatch && v.available) {
            available = true;
            break;
          }
        }

        if (available) {
          btn.classList.remove('oddit-section-main-product__size-option--unavailable');
        } else {
          btn.classList.add('oddit-section-main-product__size-option--unavailable');
        }
      });
    }

    // Run once on init
    updateSizeAvailability();

    // Sync gallery + ATC + price to an *explicitly* URL-selected variant only.
    // When the URL carries ?variant=<id> (a deep link, or a collection card
    // whose featured image is a specific variant's image), reconcile the UI
    // to that variant's hero / thumbnail. When there is NO variant param,
    // leave the server-rendered gallery alone: Liquid now leads with the
    // first-available variant's featured image and marks its thumbnail
    // active (#ELA-156), so the PDP opens already showing the correct
    // variant's first image — overriding here would just rebuild what
    // Liquid already painted.
    var hasVariantParam = false;
    try {
      hasVariantParam = new URL(window.location.href).searchParams.has('variant');
    } catch (e) {}
    if (hasVariantParam) {
      var initialVariant = findMatchingVariant();
      if (initialVariant && initialVariant.featured_image) {
        updateMainProductUI(initialVariant);
      }
    }

    /* ==========================================================================
       Async Add to Cart
       ========================================================================== */

    function setAtcError(message) {
      var errEl = sectionEl.querySelector('[data-oddit-atc-error]');
      if (!errEl) return;
      errEl.textContent = message || '';
      errEl.hidden = !message;
    }

    function formatCartAddError(data) {
      if (!data) return 'Unable to add to bag.';
      if (typeof data.description === 'string') return data.description;
      if (typeof data.message === 'string') return data.message;
      return 'Unable to add to bag.';
    }

    var atcForm = sectionEl.querySelector('.oddit-section-main-product__atc form');
    if (atcForm && !atcForm.dataset.odditAsyncAtcBound) {
      atcForm.dataset.odditAsyncAtcBound = '1';
      atcForm.addEventListener('submit', function (evt) {
        evt.preventDefault();

        var atcBtn = sectionEl.querySelector('.oddit-section-main-product__atc-button');
        var idInput = sectionEl.querySelector('input[name="id"]');
        if (!idInput || !atcBtn) return;

        var variantId = parseInt(idInput.value, 10);
        if (isNaN(variantId)) return;

        if (atcBtn.disabled) return;

        setAtcError('');

        var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        var originalLabel = atcBtn.textContent;

        atcBtn.disabled = true;
        atcBtn.classList.add('is-loading');
        atcBtn.textContent = 'ADDING...';

        fetch(rootUrl + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        })
          .then(function (response) {
            return response.json().then(function (json) {
              if (!response.ok) {
                throw new Error(formatCartAddError(json));
              }
              return json;
            });
          })
          .then(function () {
            return fetch(rootUrl + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
              return r.json();
            });
          })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
            var refreshPromise = Promise.resolve();
            if (window.odditCartDrawer && typeof window.odditCartDrawer.refresh === 'function') {
              refreshPromise = window.odditCartDrawer.refresh().catch(function () {});
            }
            return refreshPromise;
          })
          .then(function () {
            if (window.odditCartDrawer && typeof window.odditCartDrawer.open === 'function') {
              window.odditCartDrawer.open();
            }
            atcBtn.classList.remove('is-loading');
            var v = findMatchingVariant();
            updateMainProductUI(v);
          })
          .catch(function (err) {
            atcBtn.classList.remove('is-loading');
            atcBtn.textContent = originalLabel;
            atcBtn.disabled = false;
            var msg =
              err && typeof err.message === 'string' && err.message
                ? err.message
                : 'Unable to add to bag. Please try again.';
            setAtcError(msg);
          });
      });
    }

    /* ==========================================================================
       Thumbnail Scroll Indicator
       ========================================================================== */

    var thumbScrollDown = sectionEl.querySelector('.oddit-section-main-product__thumb-scroll');
    var thumbScrollUp = sectionEl.querySelector('.oddit-section-main-product__thumb-scroll-up');
    var thumbContainer = sectionEl.querySelector('.oddit-section-main-product__thumbnails');

    if (thumbContainer) {
      if (thumbScrollDown) {
        thumbScrollDown.addEventListener('click', function () {
          thumbContainer.scrollBy({ top: 184, behavior: 'smooth' });
        });
      }

      if (thumbScrollUp) {
        thumbScrollUp.addEventListener('click', function () {
          thumbContainer.scrollBy({ top: -184, behavior: 'smooth' });
        });
      }

      thumbContainer.addEventListener('scroll', function () {
        if (thumbScrollUp) {
          thumbScrollUp.classList.toggle(
            'oddit-section-main-product__thumb-scroll-up--visible',
            thumbContainer.scrollTop > 0,
          );
        }
        if (thumbScrollDown) {
          var atBottom = thumbContainer.scrollTop + thumbContainer.clientHeight >= thumbContainer.scrollHeight - 2;
          thumbScrollDown.style.opacity = atBottom ? '0' : '';
          thumbScrollDown.style.pointerEvents = atBottom ? 'none' : '';
        }
      });
    }
  }

  /* ==========================================================================
     Mobile tap-to-zoom viewer (ELA-178)
     A full-resolution, pinch/double-tap zoomable overlay so mobile shoppers
     get a crisp zoom instead of pinch-zooming the low-res inline image.
     ========================================================================== */

  // Highest-resolution source available for a gallery image.
  function getZoomSrc(img) {
    if (!img) return '';
    if (img.srcset) {
      var best = '';
      var bestW = 0;
      img.srcset.split(',').forEach(function (part) {
        var seg = part.trim().split(/\s+/);
        var w = parseInt((seg[1] || '0').replace('w', ''), 10) || 0;
        if (w > bestW) {
          bestW = w;
          best = seg[0];
        }
      });
      if (best) return best;
    }
    var src = img.currentSrc || img.src || '';
    return src.replace(/([?&]width=)\d+/, '$11500');
  }

  // Build the shared overlay + gesture handling once; return its open() fn.
  function ensureZoomViewer() {
    if (window.__odditZoomOpen) return window.__odditZoomOpen;

    var overlay = document.createElement('div');
    overlay.className = 'oddit-mobile-zoom';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Zoomed product image');
    overlay.innerHTML =
      '<button type="button" class="oddit-mobile-zoom__close" aria-label="Close zoom">' +
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="#000" stroke-width="1.6" stroke-linecap="round"/></svg>' +
      '</button>' +
      '<div class="oddit-mobile-zoom__stage"><img class="oddit-mobile-zoom__img"></div>' +
      '<span class="oddit-mobile-zoom__hint">Pinch or double-tap to zoom</span>';
    document.body.appendChild(overlay);

    var stage = overlay.querySelector('.oddit-mobile-zoom__stage');
    var img = overlay.querySelector('.oddit-mobile-zoom__img');
    var closeBtn = overlay.querySelector('.oddit-mobile-zoom__close');
    var hint = overlay.querySelector('.oddit-mobile-zoom__hint');

    var minScale = 1;
    var maxScale = 4;
    var scale = 1;
    var tx = 0;
    var ty = 0;
    var startDist = 0;
    var startScale = 1;
    var startX = 0;
    var startY = 0;
    var startTx = 0;
    var startTy = 0;
    var lastTap = 0;

    function apply() {
      img.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
    }

    function clampPan() {
      var rect = stage.getBoundingClientRect();
      var maxX = Math.max(0, (img.offsetWidth * scale - rect.width) / 2);
      var maxY = Math.max(0, (img.offsetHeight * scale - rect.height) / 2);
      tx = Math.min(maxX, Math.max(-maxX, tx));
      ty = Math.min(maxY, Math.max(-maxY, ty));
    }

    function distance(a, b) {
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    }

    stage.addEventListener(
      'touchstart',
      function (e) {
        if (e.touches.length === 2) {
          startDist = distance(e.touches[0], e.touches[1]);
          startScale = scale;
        } else if (e.touches.length === 1) {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          startTx = tx;
          startTy = ty;
        }
      },
      { passive: false },
    );

    stage.addEventListener(
      'touchmove',
      function (e) {
        if (e.touches.length === 2) {
          e.preventDefault();
          scale = Math.min(maxScale, Math.max(minScale, startScale * (distance(e.touches[0], e.touches[1]) / startDist)));
          clampPan();
          apply();
        } else if (e.touches.length === 1 && scale > 1) {
          e.preventDefault();
          tx = startTx + (e.touches[0].clientX - startX);
          ty = startTy + (e.touches[0].clientY - startY);
          clampPan();
          apply();
        }
      },
      { passive: false },
    );

    stage.addEventListener('touchend', function (e) {
      if (e.touches.length === 0) {
        var now = Date.now();
        if (now - lastTap < 300) {
          scale = scale > 1 ? 1 : 2.5;
          tx = 0;
          ty = 0;
          clampPan();
          apply();
          lastTap = 0;
        } else {
          lastTap = now;
        }
      }
      if (scale <= 1) {
        tx = 0;
        ty = 0;
        apply();
      }
    });

    function close() {
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      img.removeAttribute('src');
    }

    closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });

    function open(src, alt) {
      if (!src) return;
      scale = 1;
      tx = 0;
      ty = 0;
      img.src = src;
      img.removeAttribute('alt');
      apply();
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (hint) {
        hint.style.opacity = '1';
        setTimeout(function () {
          hint.style.opacity = '0';
        }, 1800);
      }
    }

    window.__odditZoomOpen = open;
    return open;
  }

  function initMobileZoom(sectionEl) {
    if (!sectionEl || sectionEl.getAttribute('data-mobile-zoom') !== 'true') return;
    if (sectionEl._mobileZoomBound) return;
    sectionEl._mobileZoomBound = true;

    var mobileMq = window.matchMedia('(max-width: 989px)');
    var open = ensureZoomViewer();

    sectionEl
      .querySelectorAll('.oddit-section-main-product__carousel-slide[data-media-type="image"]')
      .forEach(function (slide) {
        slide.addEventListener('click', function () {
          if (!mobileMq.matches) return;
          var slideImg = slide.querySelector('img');
          if (slideImg) open(getZoomSrc(slideImg), slideImg.alt);
        });
      });
  }

  /* Initialize on page load */
  var section = document.querySelector('.oddit-section-main-product');
  initSection(section);

  /* Re-initialize on Shopify theme editor section load */
  document.addEventListener('shopify:section:load', function (event) {
    var loadedSection = event.target.querySelector('.oddit-section-main-product');
    if (!loadedSection) {
      loadedSection = event.target.closest('.oddit-section-main-product');
    }
    initSection(loadedSection);
  });
})();
